/**
 * MessengerHelper
 * params
 * -iFrame Html
 */

import DOMPurify from 'dompurify';
import tplIframe from './messengerHelper.html';
import { sanitizeObject, stringToHTML, isFireFox, cloneObjectPropsOnly, isIphone } from 'util';
import { ALLOWED_PLUGINS, filterIframeAllowAttribute, isValidURL } from 'util';
import { verifyOrigin } from 'verifyorigin';

Genesys('registerPlugin', 'MessengerHelper', MessengerHelper => {
  const $$ = MessengerHelper.data;
  let bVendorsPlugin = false;
  let bMessengerCleared = false;
  const aAPIOrigins = []; // holds any API origins received from API urls in messages, example fileupload url, etc
  const sDocumentDirection = document.dir ? document.dir.toLowerCase() : 'ltr'; /**Set 'ltr' as default document direction to fix the sideSpace and bottomSpace values are not getting applied through configure command when the alignment is auto and documentDirection is not present or empty */
  const CLEARED_REJECTION_MESSAGE = 'Messenger is cleared. Please reload the page to reinitialize.';

  $$('iFrameLoaded', false);

  /** Disable eslint unencoded input used in HTML context. We don't inject external html here. */
  let iframeHtml = tplIframe,     // eslint-disable-line
    ws = '', fileUploadSource = '';

  const oPosition = { alignment: 'auto', sideSpace: 20, bottomSpace: 12 };
  const iMessengerLauncherSpace = 92;
  const iMessengerBottomPadding = 10;
  const bFireFox = isFireFox();
  const oLauncher = {
      height: 0,
      width: 0,
    },
    oMess = {
      height: 0,
      width: 0,
    },
    oEngage = {
      height: 0,
      width: 0,
    },
    oToast = {
      height: 0,
      width: 0,
    };
  let customLabelUrl = '';

  /** Local functions **/

  /**
   * Set the origins from dynamic API URLs received from messages, ex: presignedUrl for file uploads
   * * @param {string} url
   * */
  function setAPIOrigins(url) {
    if (!isValidURL(url)) return;
    const oURL = new URL(url);

    if (oURL.origin && !aAPIOrigins.includes(oURL.origin)) {
      aAPIOrigins.push(oURL.origin);
      MessengerHelper.data('apiOrigins', aAPIOrigins);
    }
  }

  /**
   * Function that takes existing total dimension, adds new app's dimension to it and returns the total dimension
   * @param {object} oApp
   * @param {dom} ndIframe
   */
  function getAppDimensions(oApp, ndIframe) {
    const { width, height, addClass, removeClass } = oApp;

    let iHeight = 0, iWidth = 0;

    if (typeof height === 'string') iHeight = `calc(${height})`;

    if (typeof height === 'number') iHeight = iHeight + height;

    if (ndIframe && addClass) addClass.map(value => value && ndIframe.classList.add(value));

    if (ndIframe && removeClass) removeClass.map(value => value && ndIframe.classList.remove(value));

    if (width) iWidth = width;

    return {
      iHeight,
      iWidth,
    };
  }

  /**
   * Function that sets the total iframe dimension
   * @param {dom} ndIframe
   * @param {number} iTotalWidth
   * @param {number} iTotalHeight
   */

  function setIframeDimension(ndIframe, iTotalWidth, iTotalHeight) {
    /** If width is 72 and less, then we need to apply 'important' so it doesn't get over-riden by mxg-responsive css */
    if (typeof iTotalWidth === 'number') ndIframe.style.setProperty('width', `${iTotalWidth}px`, iTotalWidth > 72 ? '' :'important');

    if (typeof iTotalHeight === 'number') ndIframe.style.setProperty('height', `${iTotalHeight}px`, 'important');

    if (typeof iTotalHeight === 'string') ndIframe.style.height = iTotalHeight;
  }

  /**
   * function that sets the new position values
   */
  function setPositionValues(position) {
    //set the messenger config position
    const { sideSpace, bottomSpace, alignment } = position || {};

    if (sideSpace!=='undefined' && typeof sideSpace === 'number') oPosition.sideSpace = sideSpace;/**sideSpace and bottomSpace values should support 0 as well*/
    if (bottomSpace!=='undefined' && typeof bottomSpace === 'number') oPosition.bottomSpace = bottomSpace;
    if (alignment && typeof alignment === 'string') oPosition.alignment = alignment;
  }

  /**
   * function that binds to WebSocket events
   */
  function bindSocketEvents() {
    if (ws) {
      ws.onMessage.addListener((e) => {
        const oData = JSON.parse(e);
        const { body } = oData || {};
        const { url } = body || {};

        if (url) setAPIOrigins(url); // WEBMESS-1813: Cache any urls. Later when making requests, we compare against these when making requests.

        MessengerHelper.publish('onWebSocketMessage', oData);
      });

      ws.onClose.addListener((e) => {
        MessengerHelper.publish('onWebSocketClose', {
          code: e.code,
          reason: e.reason,
          returnValue: e.returnValue,
          timeStamp: e.timeStamp,
          type: e.type,
          wasClean: e.wasClean,
        });
      });

      ws.onError.addListener((error) => {
        if (typeof error === 'object') {
          const oError = cloneObjectPropsOnly(error); // removes any methods that this object may contain

          MessengerHelper.publish('onWebSocketError', oError);
        } else if (typeof error === 'string') {
          MessengerHelper.publish('onWebSocketError', JSON.parse({ error }));
        }
      });
    }
  }


  /**
  * WEBMESS-289, WEBMESS-1579: [accessibility] - apply the aria properties only on iphones
  * to avoid background page navigation while using with screen readers
  */
  function setAriaAttributes() {
    const ndMessengerWrapper = document.getElementById('genesys-messenger'), //wrapper div of the iframes
      ndMessengerFrame = document.getElementById('genesys-mxg-container-frame'), // messenger iframe
      ndIframe = document.getElementById('genesys-mxg-frame'); // launcher iframe

    // check to evaluate messenger iframe contains homescreen/conversation screen to apply the aria properties
    const bConversationScreen = ndMessengerFrame && ndMessengerFrame.classList.contains('genesys-mxg-conversation'),
      bHomeScreen = ndMessengerFrame && ndMessengerFrame.classList.contains('genesys-mxg-homescreen');

    if (isIphone() && (bConversationScreen || bHomeScreen)) {
    //prevent the voiceover focus on launcher when messenger is displayed in iphone
      if (ndIframe) {
        ndIframe.setAttribute('hidden', '');
      }
      ndMessengerWrapper.setAttribute('aria-modal', 'true');
      ndMessengerWrapper.setAttribute('role', 'dialog');
    } else {
      if (ndMessengerWrapper) {
        if (ndIframe) {
          if (ndIframe.hasAttribute('hidden'))
            ndIframe.removeAttribute('hidden');
        }
        if (ndMessengerWrapper.hasAttribute('aria-modal'))
          ndMessengerWrapper.removeAttribute('aria-modal');
        if (ndMessengerWrapper.hasAttribute('role') && !isIphone())
        // removal of role dialog dynamically on iphone causes voiceover focus loss.
          ndMessengerWrapper.removeAttribute('role');
      }
    }
  }


  /**
   * function returns dimensions for a given element
   */
  function getDimension(ndContainer) {
    return {
      bottom: window.getComputedStyle(ndContainer).getPropertyValue('bottom') || '',
      top: window.getComputedStyle(ndContainer).getPropertyValue('top') || '',
      height: window.getComputedStyle(ndContainer).getPropertyValue('height') || '',
      width: window.getComputedStyle(ndContainer).getPropertyValue('width') || '',
    };
  }

  /**
   * function returns empty string if given string contains the text "javascript"
   */
  function checkForJavascript(string) {
    if (string && !!string.toLowerCase().match(/javascript/))
      return '';
    else
      return string;
  }

  //Subscribe to master cleared event and reject all other commands after
  MessengerHelper.subscribe('Messenger.cleared', () => {
    const ndLauncherIframe = document.getElementById('genesys-mxg-frame');
    const ndMessengerFrame = document.getElementById('genesys-mxg-container-frame');

    if (ndLauncherIframe) setIframeDimension(ndLauncherIframe, 0, 0);
    if (ndMessengerFrame) setIframeDimension(ndMessengerFrame, 0, 0);

    bMessengerCleared = true;
    MessengerHelper.publish('cleared');
  });

  MessengerHelper.subscribe('ServiceDiscovery.ready', () => {
    // [WEBMESS-16] Get the version from browser global if present. Remove this before releasing
    const sVersion = window._webMessVersion ? `/${window._webMessVersion}` : '';
    const sMessengerHTML = `${MessengerHelper.data(
      'ServiceDiscovery.uris.staticAssets'
    )}/messenger${sVersion}/messenger.html`;
    const sMessengerRendererHTML = `${MessengerHelper.data(
      'ServiceDiscovery.uris.staticAssets'
    )}/messenger${sVersion}/messenger-renderer.html`;


    customLabelUrl = `${MessengerHelper.data(
      'ServiceDiscovery.uris.configs'
    )}`;
    /** False positive - disable eslint HTML passed into function rule. We don't inject external html here. */
    iframeHtml = iframeHtml.replace('{url}', sMessengerHTML);   // eslint-disable-line
    iframeHtml = iframeHtml.replace('{messenger-renderer-url}', sMessengerRendererHTML);  // eslint-disable-line
  });

  MessengerHelper.registerCommand('open', e => {  // eslint-disable-line
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else if ($$('iFrameLoaded')) {
      e.reject('Messenger iframe is already open');
    } else {
      const { messenger } = e.data || {};
      const { preview } = messenger || {};

      const deploymentConfig =
        MessengerHelper.data('GenesysJS.deploymentConfig') || {};
      const snippetConfig =
        MessengerHelper.data('GenesysJS.snippetConfig') || {};

      const { position, messenger: oMessengerConfig } = deploymentConfig || {};
      const { launcherButton } = oMessengerConfig || {};
      const { visibility } = launcherButton || {};

      //set the messenger config position
      if (position) {
        setPositionValues(position);
      }

      if (visibility && visibility.toLowerCase() === 'on') {
        iframeHtml = iframeHtml.replace('messengerFrameBottomSpace', "92px");    // eslint-disable-line
      } else {
        iframeHtml = iframeHtml.replace('messengerFrameBottomSpace', `${oPosition.bottomSpace}px`);   // eslint-disable-line
      }

      //apply the nearest vertical border and bottom pixels from config
      iframeHtml = iframeHtml.replace('sideSpace', `${oPosition.sideSpace}px`);    // eslint-disable-line
      iframeHtml = iframeHtml.replace('bottomSpace', `${oPosition.bottomSpace}px`);   // eslint-disable-line

      // inject iframe into the parent page
      document.body.appendChild(stringToHTML(iframeHtml));

      // configure Messenger when it is not running in preview mode
      if (!preview) {
        MessengerHelper.subscribe('Messenger.ready', () => {
          MessengerHelper.command('Messenger.configure', {
            snippetConfig,
            deploymentConfig,

            // TODO: remove once the plugin has been updated to use deploymentConfig/snippetConfig
            deploymentId: snippetConfig.deploymentId,
            config: { ...deploymentConfig.messenger, documentDirection: sDocumentDirection },
          });
        });
      }

      $$('iFrameLoaded', true);

      e.resolve();
    }
  });

  MessengerHelper.registerCommand('update', e => {
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else {
      const { messenger } = e.data || {};
      const { preview } = messenger || {};

      const deploymentConfig =
          MessengerHelper.data('GenesysJS.deploymentConfig') || {};
      const snippetConfig =
          MessengerHelper.data('GenesysJS.snippetConfig') || {};

      // configure Messenger when it is not running in preview mode
      if (!preview) {
        MessengerHelper.command('Messenger.configure', {
          snippetConfig,
          deploymentConfig,

          // TODO: remove once the plugin has been updated to use deploymentConfig/snippetConfig
          deploymentId: snippetConfig.deploymentId,
          config: { ...deploymentConfig.messenger, documentDirection: sDocumentDirection },
        });
      }

      e.resolve();
    }
  });

  /** Internal only - Do not document */
  /**
   * Commands
   * --------
   * setDimension
   * getDimension
   * setPositions
   * injectFrame
   *
   * Commands (WebSockets)
   * ---------------------
   * openWebSocket
   * closeWebSocket
   * sendOverSocket
   * getWebSocketData
   *
   * Commands (Axios)
   * ----------------
   * makeRequest
   * uploadRequest
   * cancelRequest
   *
   * Events
   * ------
   * onWebSocketMessage
   * onWebSocketClose
   * onWebSocketError
   * uploadProgress
   *
   */


  // Append a html app in its own iframe
  MessengerHelper.registerCommand('injectFrame', e => {
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else if (e && ALLOWED_PLUGINS.includes(e.commander)) {
      const { htmlUrl, styles, title, name, id, allow } = e.data || {};

      const oURL = new URL(htmlUrl);

      if (verifyOrigin(oURL.origin)) { // validate the URL is a URL and from a whitelisted origin (see utils.js)
        // Create iframe node
        const iframe = document.createElement('iframe');

        // Populate iframe properties
        iframe.src = oURL.origin + oURL.pathname + oURL.search; // Rebuild URL from URL properties
        iframe.title = checkForJavascript(title || '');
        iframe.name = checkForJavascript(name || '');
        iframe.id = checkForJavascript(id);
        iframe.allowFullscreen = true;

        if (allow) {
          iframe.allow = filterIframeAllowAttribute(allow);
        }

        // Append iframe styles
        iframe.classList.add('genesys-mxg-frame'); // default existing common class

        if (styles && Object.keys(styles)) {
          Object.keys(styles).forEach((propertyName) => {
            iframe.style[propertyName] = styles[propertyName];
          });
        }

        // Convert iframe node to an HTML string and sanitize the HTML string
        const sCleanHTML = DOMPurify.sanitize(iframe.outerHTML, { ALLOWED_TAGS: ['iframe'], ADD_ATTR: ['src', 'allowfullscreen', 'title', 'class', 'name', 'id', 'allow'] });

        // Append the sanitized HTML into the Messenger container
        document.getElementById('genesys-messenger').insertAdjacentHTML('beforeend', sCleanHTML);
        e.resolve();
      } else {
        e.reject();
      }
    } else {
      e.reject();
    }
  });

  // To set bottom position of Launcher/Messenger based on the position config
  MessengerHelper.registerCommand('setPositions', e => {
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else {
      const { showLauncher } = e.data || {};
      const deploymentConfig =
        MessengerHelper.data('GenesysJS.deploymentConfig') || {};
      const { position, messenger: oMessengerConfig } = deploymentConfig || {};
      const { launcherButton } = oMessengerConfig || {};
      const { visibility } = launcherButton || {};
      const ndMessengerFrame = document.getElementById('genesys-mxg-container-frame');

      //set the messenger config position
      if (position) {
        setPositionValues(position);
      }

      if (visibility && visibility.toLowerCase() === 'ondemand') {
        if (showLauncher) {
          ndMessengerFrame.style.bottom = `${iMessengerLauncherSpace}px`;
        } else {
          ndMessengerFrame.style.bottom = `${oPosition.bottomSpace}px`;
        }
      }
    }
  });


  /**
   * To get frames dimensions while zooming -  WEBMESS-1399
   * @return {
   *  messengerFrame: {
   *    bottom,
   *    top,
   *    height,
   *    width
   *  },
   * launcherFrame: {
   *    bottom,
   *    top,
   *    height,
   *    width
   *  }
   * }
   */
  MessengerHelper.registerCommand('getDimension', e => {
    const ndLauncherIframe = document.getElementById('genesys-mxg-frame');
    const ndMessengerFrame = document.getElementById('genesys-mxg-container-frame');
    const styles = {
      messengerFrame: {},
      launcherFrame: {},
    };

    if (ndMessengerFrame && ndMessengerFrame.style) {
      styles.messengerFrame = getDimension(ndMessengerFrame);
    }

    if (ndLauncherIframe && ndLauncherIframe.style) {
      styles.launcherFrame = getDimension(ndLauncherIframe);
    }

    e.resolve(styles);
  });

  /**
   * sets frame width and height
   * MessengerFrame - Engage, Conversation/Home app
   * LauncherFrame - Launcher button
   * CobrowseFrame - TODO
   */
  MessengerHelper.registerCommand('setDimension', e => {
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else {
      const ndIframe = document.getElementById('genesys-mxg-frame');
      const ndMessengerFrame = document.getElementById('genesys-mxg-container-frame');
      const { engage, launcher, conversations, toaster } = e.data;
      let bDimChanged = false;

      // Launcher button dimensions
      if (launcher) {
        const { width, height, addClass, removeClass } = launcher || {};

        if (typeof height === 'number' && height != oLauncher.height) bDimChanged = true;

        oLauncher.height = height;
        oLauncher.width = width;

        /**WEBMESS-787 If the laucher object has addClass/removeClass params, add or remove the classes from iFrame like Conversations object */

        if (ndIframe && addClass) addClass.map(value => value && ndIframe.classList.add(value));

        if (ndIframe && removeClass) removeClass.map(value => value && ndIframe.classList.remove(value));

        setIframeDimension(ndIframe, width, height);
      }

      // Engage app dimensions
      if (engage) {
        const { width, height } = engage;
        let iTotalHeight = height;
        let iTotalWidth = width;

        if (typeof height === 'number' && height != oEngage.height) bDimChanged = true;

        // store locally what ever dimensions you get.
        oEngage.height = height;
        oEngage.width = width;

        // when both Toaster and Enage are present, combine their height
        if (oToast.height && height) {
          iTotalHeight = oToast.height + height;
        }

        if (oToast.width && width) {
          iTotalWidth = oToast.width;
        }

        setIframeDimension(ndMessengerFrame, iTotalWidth, iTotalHeight);
      }

      // Conversation app dimensions
      if (conversations) {
        const { iHeight, iWidth } = getAppDimensions(conversations, ndMessengerFrame);

        if (typeof iHeight === 'number' && iHeight != oMess.height) bDimChanged = true;

        oMess.height = iHeight;
        oMess.width = iWidth;

        setIframeDimension(ndMessengerFrame, iWidth, iHeight);
      } else {
      // oMess.height = 0;
      // oMess.width = 0;
      }

      if (toaster) {
        let { width: toasterWidth, height: toasterHeight } = toaster;
        const { addClass, removeClass } = toaster;

        // store locally what ever dimensions you get.
        oToast.height = toasterHeight;
        oToast.width = toasterWidth;

        if (typeof toasterHeight === 'number' && toasterHeight != oMess.height) bDimChanged = true;

        // Because toaster appears inside Messenger container, apply actual Messenger dimensions.
        if (oMess.height) toasterHeight = oMess.height;
        if (oMess.width) toasterWidth = oMess.width;

        setIframeDimension(ndMessengerFrame, toasterWidth, toasterHeight);

        if (ndIframe && addClass) addClass.map(value => value && ndIframe.classList.add(value));
        if (ndIframe && removeClass) removeClass.map(value => value && ndIframe.classList.remove(value));
      }

      if (bDimChanged) {
        MessengerHelper.publish('dimensions', {
          messenger: {
            height: oMess.height,
            width: oMess.width,
          },
          launcher: {
            height: oLauncher.height,
            width: oLauncher.width,
          },
          toaster: {
            height: oToast.height,
            width: oToast.width,
          },
          engage: {
            height: oEngage.height,
            width: oEngage.width,
          },
        });
      }

      // WEBMESS-289: adding and removing aria properties to the iframe parent div to avoid background page navigation in iphones while using with voiceover
      setAriaAttributes();

      //  set width and height of iFrame
      if (ndIframe || ndMessengerFrame) {
        e.resolve();
      } else {
        e.reject('No styles or className provided for Messenger iFrame');
      }
    }
  });

  MessengerHelper.registerCommand('setDirection', (e) => {
    const ndIframe = document.getElementById('genesys-mxg-frame');
    const ndMessengerFrame = document.getElementById('genesys-mxg-container-frame');
    const { direction, position } = e.data || {};

    if (position) {//update position based on new config changes
      setPositionValues(position);
    }
    const { alignment } = oPosition;

    function setToLeft() { //update styles to force Iframe left
      // For Launcher iframe
      ndIframe.style.right = 'unset';
      ndIframe.style.left = `${oPosition.sideSpace}px`;
      ndIframe.style.bottom = `${oPosition.bottomSpace}px`;

      // For Messenger iframe
      ndMessengerFrame.style.right = 'unset';
      ndMessengerFrame.style.left = `${oPosition.sideSpace}px`;
      ndMessengerFrame.style.bottom = `${ndIframe.clientHeight ? ndIframe.clientHeight + iMessengerBottomPadding + oPosition.bottomSpace : oPosition.bottomSpace}px`;
    }

    function setToRight() { //update styles to force Iframe right
      // For Launcher iframe
      ndIframe.style.left = 'unset';
      ndIframe.style.right = `${oPosition.sideSpace}px`;
      ndIframe.style.bottom = `${oPosition.bottomSpace}px`;

      // For Messenger iframe
      ndMessengerFrame.style.left = 'unset';
      ndMessengerFrame.style.right = `${oPosition.sideSpace}px`;
      ndMessengerFrame.style.bottom = `${ndIframe.clientHeight ? ndIframe.clientHeight + iMessengerBottomPadding + oPosition.bottomSpace : oPosition.bottomSpace}px`;
    }

    function setDirection(sDirection) {
      switch (sDirection) {
        case 'rtl':
          setToLeft();
          break;
        case 'ltr':
          setToRight();
          break;
        default:
          break;
      }
    }

    /*
      * Set iframe position based on the config alignment value
      * right, left or auto: detect position based on document.dir
    */
    if (ndIframe && (position || sDocumentDirection)) {
      if (alignment === 'auto') {
        if (sDocumentDirection) {
          setDirection(sDocumentDirection);
          e.resolve();
        } else {
          setDirection(direction);
          e.resolve();
        }
      } else if (alignment === 'left') {
        setToLeft();
        e.resolve();
      } else if (alignment === 'right') {
        setToRight();
        e.resolve();
      }
    } else {
      e.reject('No alignment, styles or direction provided for Messenger iFrame');
    }
  });

  MessengerHelper.registerCommand('openWebSocket', e => {
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else {
      function onOpened(event) {
        bindSocketEvents();
        const data = sanitizeObject(event);

        e.resolve(data);
      }

      function fOpenWebSocket() {
        const iOpenTimeout = bFireFox ? 4500 : 0; // milliseconds WEBMESS-1391
        const { endPoint } = e.data || {};

        MessengerHelper.command('GenesysVendors.WebSocket', { endPoint }).then((WebSocketAsPromised) => {
          if (ws) ws.removeAllListeners(); // remove any previous instance if present, especially during reconnecting

          ws = new WebSocketAsPromised(endPoint, { timeout: iOpenTimeout, connectionTimeout: iOpenTimeout });

          ws.open().then((event) => {
            onOpened(event);
          }).catch((error) => {
            const { message } = error || {};

            if (message && message.indexOf('1006') && bFireFox) {
            // WEBMESS-1391: Try second attempt for firefox only - WebSocket closed with reason:  (1006).

              ws.open().then((evt) => {
                onOpened(evt);
              }).catch((err) => {
                e.reject(err);
              });
            } else {
              e.reject(error);
            }
          });
        }).catch(({ error }) => {
          e.reject(error);
        });
      }

      if (e && e.commander === 'MessagingService') {
      /**
       * Check if Vendors plugin is loaded, if not load it and open socket connection
       */

        if (bVendorsPlugin) {
          fOpenWebSocket();
        } else {
          Genesys('loadPlugin', 'GenesysVendors', {}, false, () => {
            bVendorsPlugin = true;

            fOpenWebSocket();
          });
        }
      } else {
        e.reject('Unauthorized Access! Only MessagingService plugin is allowed to access this command.');
      }
    }
  });

  MessengerHelper.registerCommand('closeWebSocket', e => {
    if (e && e.commander === 'MessagingService') {
      const { code, reason } = e.data || {};

      if (ws && ws.close) {
        if (code) {
          ws.close(code, reason).catch(() => {});
        } else {
          ws.close().catch(() => {});
        }
      }
    } else {
      e.reject('Unauthorized Access! Only MessagingService plugin is allowed to access this command.');
    }
  });

  MessengerHelper.registerCommand('sendOverSocket', e => {
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else if (e && e.commander === 'MessagingService') {
      ws.send(JSON.stringify(e.data || {}));

      e.resolve();
    } else {
      e.reject('Unauthorized Access! Only MessagingService plugin is allowed to access this command.');
    }
  });

  MessengerHelper.registerCommand('getWebSocketData', e => {
    if (ws) {
      e.resolve({
        isClosed: ws.isClosed,
        isOpened: ws.isOpened,
        isClosing: ws.isClosing,
      });
    } else {
      e.reject('No WebSocket connection available.');
    }
  });

  MessengerHelper.registerCommand('makeRequest', e => {
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else {
      const { url, method, responseType, headers, ...others } = e.data || {};

      function fMakeRequest() {
        MessengerHelper.command('GenesysVendors.Axios', { url }).then((axios) => {
          axios({
            url,
            method,
            responseType: responseType || 'json',
            headers,
            ...others,
          }).then((response) => {
            e.resolve({
              data: response.data,
              headers: response.headers,
              status: response.status,
              statusText: response.statusText,
            });
          }, (error) => {
            const oError = error && error.response ? error.response : error;

            e.reject(JSON.parse(JSON.stringify(oError || {})));
          }).catch((error) => {
            e.reject(JSON.parse(JSON.stringify(error || {})));
          });
        }).catch(({ error }) => {
          e.reject(JSON.parse(JSON.stringify(error || {})));
        });
      }

      if (e && ALLOWED_PLUGINS.includes(e.commander)) {
      /**
       * Check if Vendors plugin is loaded, if not load it and then make the request
       */

        if (bVendorsPlugin) {
          fMakeRequest();
        } else {
          Genesys('loadPlugin', 'GenesysVendors', {}, false, () => {
            bVendorsPlugin = true;

            fMakeRequest();
          });
        }
      } else {
        e.reject('Unauthorized Access! Only MessagingService plugin is allowed to access this command.');
      }
    }
  });

  MessengerHelper.registerCommand('uploadRequest', e => {
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else {
      const { url, headers, data } = e.data || {};

      function fUploadRequest() {
        MessengerHelper.command('GenesysVendors.Axios', { url }).then((axios) => {
          const CancelToken = axios.CancelToken;

          fileUploadSource = CancelToken.source();

          axios.put(url, data, {

            headers,
            onUploadProgress: (progressEvent) => {
              const { loaded, total } = progressEvent;
              const percentage = Math.floor(loaded * 100 / total);

              MessengerHelper.publish('uploadProgress', { percentage, loaded, total });
            },
            cancelToken: fileUploadSource.token,

          }).then((response) => {
            e.resolve({
              data: response.data,
              headers: response.headers,
              status: response.status,
              statusText: response.statusText,
            });
          }, (error) => {
            e.reject(JSON.parse(JSON.stringify(error || {})));
          }).catch((error) => {
            e.reject(JSON.parse(JSON.stringify(error || {})));
          });
        });
      }

      if (e && ALLOWED_PLUGINS.includes(e.commander)) {
      /**
       * Check if Vendors plugin is loaded, if not load it and then make the request
       */

        if (bVendorsPlugin) {
          fUploadRequest();
        } else {
          Genesys('loadPlugin', 'GenesysVendors', {}, false, () => {
            bVendorsPlugin = true;

            fUploadRequest();
          });
        }
      } else {
        e.reject('Unauthorized Access! Only MessagingService plugin is allowed to access this command.');
      }
    }
  });

  MessengerHelper.registerCommand('cancelRequest', e => {
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else if (e && ALLOWED_PLUGINS.includes(e.commander)) {
      if (fileUploadSource) {
        fileUploadSource.cancel();
        fileUploadSource = ''; // reset
        e.resolve();
      }
    } else {
      e.reject('Unauthorized Access! Only MessagingService plugin is allowed to access this command.');
    }
  });

  MessengerHelper.registerCommand('getCustomLabels', (e) => {
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else {
      let customLangLabels = {};
      const { language } = e.data;
      const snippetConfig = MessengerHelper.data('GenesysJS.snippetConfig') || {};
      const { deploymentId } = snippetConfig;

      // download the custom language file only when the deploymentID and language is available
      if (deploymentId && language) {
      // download the custom language file from cdn url
        Genesys(
          'loadJSON',
          `${customLabelUrl}/${deploymentId}/${language}.json`,
          (oCustomLangStrings) => {
            try {
              if (oCustomLangStrings) {
                const customLangStrings = JSON.parse(oCustomLangStrings);

                if (customLangStrings.messenger) {
                  customLangLabels = customLangStrings.messenger;
                  e.resolve({
                    customLangLabels: customLangLabels,
                  });
                }
              }
            } catch (err) {
              e.reject(err);
            }
          }, (err) => {
          // Reject and throw an error in the console
            e.reject(err);
          }
        );
      } else {
      // Reject and throw an error in the console
        e.reject('Deployment Id or language is not available');
      }
    }
  });

  //WEBMESS-2146: getDeviceInfo command
  /**
   * Returns device information for plugins to enable device-specific functionality
   */
  MessengerHelper.registerCommand('getDeviceInfo', (e) => {
    //cSpell: ignore macintel, macppc, iemobile, cros, crios, fxios
    if (bMessengerCleared) {
      e.reject(CLEARED_REJECTION_MESSAGE);
    } else if (e && ALLOWED_PLUGINS.includes(e.commander)) {
      try {
        const sUa = navigator.userAgent || '';
        const sPlatform = navigator.platform || '';
        const nTouchPoints = navigator.maxTouchPoints || 0;

        // Device type enum
        const DEVICE_TYPES = {
          DESKTOP: 'desktop',
          MOBILE: 'mobile',
          TABLET: 'tablet',
        };

        // OS enum
        const OS_TYPES = {
          IOS: 'iOS',
          ANDROID: 'Android',
          MAC: 'Mac OS X',
          WINDOWS: 'Windows',
          CHROME_OS: 'ChromeOS',
          LINUX: 'Linux',
          UNKNOWN: 'unknown',
        };

        // Browser enum
        const BROWSER_TYPES = {
          EDGE: 'Edge',
          CHROME: 'Chrome',
          FIREFOX: 'Firefox',
          SAFARI: 'Safari',
          OPERA: 'Opera',
          IE: 'Internet Explorer',
          UNKNOWN: 'unknown',
        };

        // Platform constants
        const PLATFORMS = {
          MAC_INTEL: 'MacIntel',
          MAC_PPC: 'MacPPC',
        };

        // Mobile device identifiers
        const MOBILE_KEYWORDS = ['Mobile', 'iPhone', 'iPod', 'Android', 'BlackBerry', 'IEMobile', 'Opera Mini'];
        const TABLET_KEYWORDS = ['iPad', 'Tablet', 'tablet', 'Android'];
        const IOS_KEYWORDS = ['iPhone', 'iPad', 'iPod'];


        /// Defaults to 'desktop' if mobile/tablet detection fails
        let sDeviceType = DEVICE_TYPES.DESKTOP;
        let sOs = OS_TYPES.UNKNOWN;
        let sBrowser = BROWSER_TYPES.UNKNOWN;

        // Device type detection
        if (
          TABLET_KEYWORDS.some(tablet => sUa.includes(tablet)) && !sUa.includes('Mobile')
        ) {
          sDeviceType = DEVICE_TYPES.TABLET;
        }
        // Special case: iPadOS 13+
        else if (
          (sPlatform === PLATFORMS.MAC_INTEL || sPlatform === PLATFORMS.MAC_PPC) &&
        nTouchPoints > 2
        ) {
          sDeviceType = DEVICE_TYPES.TABLET;
        }
        // Check for mobile
        else if (
          MOBILE_KEYWORDS.some(device => sUa.includes(device))
        ) {
          sDeviceType = DEVICE_TYPES.MOBILE;
        }

        //OS detection
        if (IOS_KEYWORDS.some(device => sUa.includes(device))) {
          sOs = OS_TYPES.IOS;
        } else if (sUa.includes('Android')) {
          sOs = OS_TYPES.ANDROID;
        } else if (sPlatform.includes('Mac')) {
          sOs = nTouchPoints > 2 ? OS_TYPES.IOS : OS_TYPES.MAC;
        } else if (sPlatform.includes('Win') || sUa.includes('Windows')) {
          sOs = OS_TYPES.WINDOWS;
        } else if (sUa.includes('CrOS') || sPlatform === 'CrOS') {
          sOs = OS_TYPES.CHROME_OS;
        } else if (sPlatform.includes('Linux') || sUa.includes('Linux')) {
          sOs = OS_TYPES.LINUX;
        }

        // Detect browser
        if (sUa.includes('Edg')) {
          sBrowser = BROWSER_TYPES.EDGE;
        } else if (sUa.includes('Chrome') || sUa.includes('CriOS')) {
          sBrowser = BROWSER_TYPES.CHROME;
        } else if (sUa.includes('Firefox') || sUa.includes('FxiOS')) {
          sBrowser = BROWSER_TYPES.FIREFOX;
        } else if (sUa.includes('Safari') && !sUa.includes('Chrome')) {
          sBrowser = BROWSER_TYPES.SAFARI;
        } else if (sUa.includes('Opera') || sUa.includes('OPR')) {
          sBrowser = BROWSER_TYPES.OPERA;
        } else if (sUa.includes('MSIE') || sUa.includes('Trident')) {
          sBrowser = BROWSER_TYPES.IE;
        }

        // Screen information
        // Screen information
        const screenWidth = window.screen?.width || 0;
        const screenHeight = window.screen?.height || 0;
        const oScreenInfo = {
          width: screenWidth,
          height: screenHeight,
          isLandscape: screenWidth > screenHeight,
        };

        // Viewport information
        const oViewportInfo = {
          width: window.innerWidth || 0,
          height: window.innerHeight || 0,
        };

        // Build response object
        const deviceInfo = {
          deviceType: sDeviceType,
          isMobile: sDeviceType === DEVICE_TYPES.MOBILE,
          isTablet: sDeviceType === DEVICE_TYPES.TABLET,
          isDesktop: sDeviceType === DEVICE_TYPES.DESKTOP,
          os: sOs,
          browser: sBrowser,
          platform: sPlatform,
          userAgent: sUa,
          screen: oScreenInfo,
          viewport: oViewportInfo,
          touchEnabled: nTouchPoints > 0 || 'ontouchstart' in window,
          language: navigator.language || '',
          online: navigator.onLine !== undefined ? navigator.onLine : true,
        };

        e.resolve(deviceInfo);
      } catch (error) {
        e.reject(`Failed to retrieve device information: ${error.message}`);
      }
    } else {
      e.reject('Unauthorized Access! Only allowed plugins can access this command.');
    }
  });

  MessengerHelper.ready();
});
